import { CommonModule, formatDate } from "@angular/common";
import { Component, inject, Injectable, LOCALE_ID, OnInit, signal } from "@angular/core";
import { FormsModule, NgForm } from "@angular/forms";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { Data, Params, Router } from "@angular/router";
import { finalize, forkJoin, Observable, of } from "rxjs";
import { CurrentUserService } from "../../shared/CurrentUser.service";
import { AppRoles } from "../../shared/enums/AppRoles";
import { AppDateAdapter, APP_DATE_FORMATS } from "../../shared/format-datepicker";
import { FormControlHelper } from "../../shared/helpers/FormControlHelper";
import { ChangeDetectionService } from "../../shared/services/change-detection.service";
import { DialogService } from "../../shared/services/dialog.service";
import { LoadingService } from "../../shared/services/loading-overlay/loading-overlay.component";
import { InvoiceModalService } from "../invoice-modal/invoice-modal.service";
import { Invoice, InvoiceCreate, InvoiceStatus } from "../invoice-modal/types/Invoice";
import { QuoteModalService } from "../quote-create-modal/quote-modal.service";
import { Quote, QuotePaymentLineItemAssocUpdatesDto, QuoteStatus } from "../quote-create-modal/types/Quote";
import { VendorDetailModal } from "../vendor-detail-modal/vendor-detail-modal.component";
import { VendorDetailModalService } from "../vendor-detail-modal/vendor-detail-modal.service";
import { YesNoModal } from "../yes-no-modal/yes-no-modal.component";
import { QuoteEditModalService, VendorQuoteLineItemDto } from "./quote-edit-modal.service";
import { QuoteEditModalHelper } from "./QuoteEditModalHelper";
import { QuoteEditAssocGridBudgetYear, QuoteEditAssocGridItem } from "./types/QuoteEditAssocGridItem";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { QuoteEditModalLineItemAssocGridComponent } from "./line-item-assoc-grid/quote-edit-modal-line-item-assoc-grid.component";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatSelectModule } from "@angular/material/select";
import { MatCardModule } from "@angular/material/card";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { DocumentPickerComponent } from "../../components/document-picker/document-picker.component";
import { ScrollInsetShadowDirective } from "../../directives/scroll-inset-shadow.directive";

export interface ShowQuoteEditModalData {
  /** Required */
  VendorId: number;
  QuoteId: number;
  isFromVendor?: boolean;
}

interface QuoteCloseModalResult {
  InvoiceIdToDisplay?: number;
  QuoteId: number;
}

interface QuoteStatusDef {
  id: QuoteStatus,
  label: string,
  isEnabled: { (): boolean },
  disableGrid: boolean,
  disableDocumentChanges: boolean,
  disablePurchaseRequisitionChanges: boolean,
  disablePurchaseOrderChanges: boolean
}

@Component({
  selector: 'quote-edit-modal',
  templateUrl: 'quote-edit-modal.component.html',
  styleUrl: 'quote-edit-modal.component.less',
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
    LoadingService, ChangeDetectionService, DialogService, VendorDetailModalService,
    QuoteModalService, QuoteEditModalService, InvoiceModalService
  ],
  standalone: true,
  imports: [QuoteEditModalLineItemAssocGridComponent, DocumentPickerComponent, MatDialogModule,
    MatFormFieldModule, FormsModule, CommonModule, MatButtonModule, MatInputModule, MatIconModule,
    MatSelectModule, MatCardModule, MatDatepickerModule, MatSnackBarModule, ScrollInsetShadowDirective]
})

export class QuoteEditModalComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<QuoteEditModal, QuoteCloseModalResult>>(MatDialogRef);
  private showModalData = inject<ShowQuoteEditModalData>(MAT_DIALOG_DATA);
  private dialog = inject(MatDialog);
  private loadingService = inject(LoadingService);
  private quoteService = inject(QuoteModalService);
  private dialogService = inject(DialogService);
  private quoteEditModalService = inject(QuoteEditModalService);
  private formControlHelper = inject(FormControlHelper);
  private userService = inject(CurrentUserService)
  private snackbar = inject(MatSnackBar);
  private invoiceModalService = inject(InvoiceModalService);
  private vendorDetailModal = inject(VendorDetailModal);
  private vendorService = inject(VendorDetailModalService);
  private router = inject(Router);
  private locale_id = inject(LOCALE_ID);
  private changeDetectionService = new ChangeDetectionService(this.dialogService);
  private quoteEditModalHelper = new QuoteEditModalHelper();

  private IsNewVendor: boolean = false;
  private UserIsAdmin: boolean = false;
  private UserRegionalAdminCountries: number[] = [];
  private isLoadingDocument: boolean = false;

  private UserAccess = {
    IsPaymentAdmin: false,
    PaymentAdminCountries: <number[]>[],
    IsAdminForQuote: () => {
      const isRegionalAdmin = this.Quote()
        ? this.UserAccess.PaymentAdminCountries.indexOf(this.Quote().VendorCountryId) >= 0
        : false;

      return this.UserAccess.IsPaymentAdmin || isRegionalAdmin;
    }
  }

  protected Quote = signal<Quote>(null);
  protected isFromVendor?: boolean = this.showModalData.isFromVendor;

  private GetQuoteStatusIsEnabled(requireAdmin: boolean, allowableTransitionStatuses: QuoteStatus[]) {
    if (!this.Quote()) return false;

    if (requireAdmin && !this.UserAccess.IsAdminForQuote())
      return false;

    return allowableTransitionStatuses.indexOf(this.Quote().Status) >= 0;
  }

  protected Dropdowns = {
    QuoteStatuses: <QuoteStatusDef[]>[
      {
        id: QuoteStatus.New,
        label: null,
        isEnabled: () => this.GetQuoteStatusIsEnabled(false, [QuoteStatus.New, QuoteStatus.ReadyForReview]),
        disableGrid: false,
        disableDocumentChanges: false,
        disablePurchaseOrderChanges: true,
        disablePurchaseRequisitionChanges: true
      },
      {
        id: QuoteStatus.ReadyForReview,
        label: null,
        isEnabled: () => {
          let isEnabled = this.GetQuoteStatusIsEnabled(false, [QuoteStatus.New, QuoteStatus.ReadyForReview, QuoteStatus.Approved])

          // dont want to allow non-admins to un-approve a quote
          if (isEnabled && this.Quote().Status > QuoteStatus.ReadyForReview && !this.UserAccess.IsAdminForQuote())
            isEnabled = false;

          if (this.SubmittedForReviewInvalid())
            isEnabled = false;
          return isEnabled;
        },
        disableGrid: true,
        disableDocumentChanges: true,
        disablePurchaseOrderChanges: true,
        disablePurchaseRequisitionChanges: true
      },
      {
        id: QuoteStatus.Approved,
        label: null,
        isEnabled: () => this.GetQuoteStatusIsEnabled(true, [QuoteStatus.ReadyForReview, QuoteStatus.Approved, QuoteStatus.PurchaseRequest]),
        disableGrid: true,
        disableDocumentChanges: true,
        disablePurchaseOrderChanges: true,
        disablePurchaseRequisitionChanges: true
      },
      {
        id: QuoteStatus.PurchaseRequest,
        label: null,
        isEnabled: () => this.GetQuoteStatusIsEnabled(true, [QuoteStatus.Approved, QuoteStatus.PurchaseRequest, QuoteStatus.PurchaseOrder, QuoteStatus.NoPurchaseOrder]),
        disableGrid: true,
        disableDocumentChanges: true,
        disablePurchaseOrderChanges: true,
        disablePurchaseRequisitionChanges: false
      },
      {
        id: QuoteStatus.PurchaseOrder,
        label: null,
        isEnabled: () => this.GetQuoteStatusIsEnabled(true, [QuoteStatus.PurchaseRequest, QuoteStatus.PurchaseOrder]),
        disableGrid: true,
        disableDocumentChanges: true,
        disablePurchaseOrderChanges: false,
        disablePurchaseRequisitionChanges: true
      },
      {
        id: QuoteStatus.NoPurchaseOrder,
        label: null,
        isEnabled: () => this.GetQuoteStatusIsEnabled(true, [QuoteStatus.PurchaseRequest, QuoteStatus.NoPurchaseOrder]),
        disableGrid: true,
        disableDocumentChanges: true,
        disablePurchaseOrderChanges: true,
        disablePurchaseRequisitionChanges: true
      }
    ]
  };

  protected ActiveAgreement = {
    StartDate: "",
    EndDate: ""
  };

  protected PurchaseOrderRequired: string = "";

  protected lineItemRows = signal<QuoteEditAssocGridItem[]>([]);
  protected budgetYears = signal<number[]>([]);

  ngOnInit(): void {
    this.isFromVendor = this.showModalData.isFromVendor;

    const loadRef = this.loadingService.StartWaiting("Fetching data");

    // Load both dropdowns and quote data together
    forkJoin({
      dropdowns: this.quoteEditModalService.GetQuoteStatusDropdownList(),
      quote: this.quoteService.GetQuoteById(this.showModalData.VendorId, this.showModalData.QuoteId)
    }).subscribe(({ dropdowns, quote }) => {
      this.Dropdowns.QuoteStatuses.forEach((item) => {
        let quoteStatus = dropdowns.filter(x => x.Status == item.id)[0];
        item.label = quoteStatus.Description;
      });

      this.Quote.set(quote);
      
      if (quote.ActiveAgreementEndDate != undefined)
        this.ActiveAgreement.EndDate = formatDate(quote.ActiveAgreementEndDate, "d-LLL-y", this.locale_id);
      if (quote.ActiveAgreementStartDate != undefined)
        this.ActiveAgreement.StartDate = formatDate(quote.ActiveAgreementStartDate, "d-LLL-y", this.locale_id);

      this.PurchaseOrderRequired = quote.IsPurchaseOrderRequired ? "Yes" : "No";

      this.userService.HasRole(AppRoles.PaymentAdmin).subscribe((hasRole) => {
        this.UserAccess.IsPaymentAdmin = hasRole;
        this.UserIsAdmin = this.UserIsAdmin || hasRole;

        this.vendorService.GetVendor(this.showModalData.VendorId).subscribe((vendor) => {
          this.userService.GetPaymentAdminCountries().subscribe((countries) => {
            this.UserAccess.PaymentAdminCountries = countries;
            this.UserRegionalAdminCountries = countries;

            const isRegionalAdmin = this.IsNewVendor
              ? this.UserRegionalAdminCountries.length > 0
              : this.UserRegionalAdminCountries.find((c) => c == vendor.CountryId) != null;

            this.UserIsAdmin = this.UserIsAdmin || isRegionalAdmin;
          });
        });
      });

      this.quoteEditModalService.GetQuotePaymentLineItems(quote.VendorId, quote.Id, quote.ContactId, quote.FieldScientistId, quote.Year)
        .subscribe((dtos) => {
          loadRef.Stop();

          let grouped: Array<QuoteEditAssocGridItem> = [];
          let uniqueBudgetYears: Array<number> = [];

          dtos.forEach((lineItemDto) => {
            const dto: VendorQuoteLineItemDto = lineItemDto;
            dto.Assocs.forEach((assoc) => {
              const budgetYear: QuoteEditAssocGridBudgetYear = {
                AssocId: assoc.Id,
                BudgetYear: assoc.BudgetYear,
                QuotedAmount: assoc.QuotedAmount,
                IsLocked: assoc.QuoteId !== undefined && assoc.QuoteId !== quote.Id,
                QuoteId: assoc.QuoteId,
                QuoteNumber: assoc.QuoteNumber
              }

              if (uniqueBudgetYears.indexOf(assoc.BudgetYear) < 0)
                uniqueBudgetYears.push(assoc.BudgetYear);

              let existing = grouped.find((a) => a.ObjectiveName == assoc.ObjectiveName && a.LineItemId == dto.PaymentLineItem.Id);
              if (existing != null) {
                existing.BudgetYears.push(budgetYear);
              } else {
                existing = {
                  CurrencyName: dto.PaymentLineItem.CurrencyName,
                  LineItemId: dto.PaymentLineItem.Id,
                  Number: dto.PaymentLineItem.Number,
                  ObjectiveName: assoc.ObjectiveName,
                  ShortTitle: dto.PaymentLineItem.ShortTitle,
                  Status: dto.PaymentLineItem.Status,
                  IsCommercial: dto.PaymentLineItem.IsCommercial,
                  IsExternal: dto.PaymentLineItem.IsExternal,
                  BudgetYears: [budgetYear],
                  Comment: null,
                  ItemType: dto.PaymentLineItem.ItemType,
                  SourceSystemLink: dto.PaymentLineItem.SourceSystemLink
                };
                grouped.push(existing);
              }

              // set the row's comment
              if (existing.Comment == null && assoc.QuoteId == quote.Id)
                existing.Comment = assoc.Comment;

              existing[`by_${budgetYear.BudgetYear}`] = budgetYear;
            })
          });
          uniqueBudgetYears.sort();

          this.lineItemRows.set(grouped);
          this.budgetYears.set(uniqueBudgetYears);
          this.changeDetectionService.SetOriginalValue(this.GetChangeDetectionData());
        });
    });
  }

  private GetSelectedQuoteStatus(): QuoteStatusDef {
    if (!this.Quote()) return null;

    return this.Dropdowns.QuoteStatuses.find((a) => a.id == this.Quote().Status);
  }

  protected isGridDisabled() {
    if (!this.Quote()) return;

    return (this.Quote().Documents == null || this.Quote().Documents.length == 0) || this.GetSelectedQuoteStatus().disableGrid;
  }

  private isFeatureDisabledByStatus(predicate: { (selectedStatus: QuoteStatusDef): boolean }) {
    if (!this.Quote())
      return;
    else
      return predicate(this.GetSelectedQuoteStatus());
  }

  protected isDocumentChangeDisabled = (): boolean => this.isFeatureDisabledByStatus((a) => a.disableDocumentChanges);
  protected isPurchaseRequisitionDisabled = (): boolean => this.isFeatureDisabledByStatus((a) => a.disablePurchaseRequisitionChanges);
  protected isPurchaseOrderDisabled = (): boolean => this.isFeatureDisabledByStatus((a) => a.disablePurchaseOrderChanges);

  // Returns the budget year info from the line item grid, but only records tied to this quote.
  private GetQuotedBudgetYearInfo = (): QuotePaymentLineItemAssocUpdatesDto[] => {
    const results: QuotePaymentLineItemAssocUpdatesDto[] = [];

    this.lineItemRows().forEach((lineItem) => {
      lineItem.BudgetYears.filter((by) => this.quoteEditModalHelper.IsQuotedAmountForThisQuote(this.Quote().Id, by)).forEach((by) => {
        results.push({
          AssocId: by.AssocId,
          Comment: lineItem.Comment,
          QuotedAmount: by.QuotedAmount
        });
      });
    });

    return results;
  }

  protected GetTotalCommitedAmount(): number {
    const results = this.GetQuotedBudgetYearInfo();
    return results.length > 0 ? results.map((item) => Number(item.QuotedAmount)).reduce((acc, curr) => acc + curr, 0) : 0;
  }

  protected IsAtLeastOneDocumentRequired() {
    return this.GetQuotedBudgetYearInfo().length > 0;
  }

  private GetChangeDetectionData = () => ({
    quote: this.Quote(),
    quotedAmounts: this.GetQuotedBudgetYearInfo()
  })

  protected checkDocumentStatus = () => (this.Quote().Documents != null && this.Quote().Documents.length > 0) ? true : false;

  protected SaveQuoteAndClose = (form: NgForm) => this.Save(form, true);
  protected SaveQuote = (form: NgForm) => this.Save(form, false);

  private Save(form: NgForm, closeOnSave: boolean) {
    if (!this.formControlHelper.CheckFormIsValid(form)) return;

    if (this.isLoadingDocument == true) {
      this.snackbar.open("Document must finish uploading before saving.", null, {
        duration: 5000,
      });
      return;
    }

    const loadingRef = this.loadingService.StartWaiting("Updating Quote");
    this.quoteService.UpdateQuote(this.Quote().VendorId, this.Quote().Id, {
      ContactId: this.Quote().ContactId,
      FieldScientistId: this.Quote().FieldScientistId,
      Year: this.Quote().Year,
      PurchaseRequisitionNumber: this.Quote().PurchaseRequisitionNumber,
      PurchaseOrderNumber: this.Quote().PurchaseOrderNumber,
      PurchaseOrderStartDate: this.Quote().PurchaseOrderStartDate,
      PurchaseOrderEndDate: this.Quote().PurchaseOrderEndDate,
      Comments: this.Quote().Comments,
      Status: this.Quote().Status,
      VendorAssignedQuoteNumber: this.Quote().VendorAssignedQuoteNumber,
      PaymentLineItemAssocUpdates: this.GetQuotedBudgetYearInfo(),
      DocumentIds: this.Quote().Documents.map(doc => doc.Id)
    })
      .pipe(finalize(() => loadingRef.Stop()))
      .subscribe((result: Quote) => {
        this.Quote.set(result);
        this.changeDetectionService.SetOriginalValue(this.GetChangeDetectionData());

        if (closeOnSave) {
          this.dialogRef.close({
            QuoteId: result.Id
          });
        }
      });
  }

  protected onSafeToSaveParent(safe: boolean) {
    this.isLoadingDocument = !safe;
  }

  private SubmittedForReviewInvalid(): boolean {
    const quoteBudgetYearInfo = this.GetQuotedBudgetYearInfo();

    return quoteBudgetYearInfo.length == 0 || quoteBudgetYearInfo.every(info => info.QuotedAmount <= 0)
  }

  protected CancelModal() {
    this.CheckForChangesAndPrompt().subscribe((shouldLeave) => {
      if (!shouldLeave) return;
      this.dialogRef.close(undefined);
    });
  }

  protected DisableDelete() {
    return this.Quote()?.CanBeDeleted == null || !this.Quote().CanBeDeleted || this.changeDetectionService.HasChanges(this.GetChangeDetectionData())
  }

  protected DeleteQuote() {
    this.CheckForChangesAndPrompt().subscribe((shouldLeave) => {
      if (!shouldLeave) return;

      const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      headerTitle: "Delete Quote", message: "Are you sure you want to delete this Quote?",
      yesText: "Yes",
      noText: "No"
    };
      this.dialog.open(YesNoModal, dialogConfig).afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          const loadingRef = this.loadingService.StartWaiting("Deleting Quote");
          this.quoteEditModalService.DeleteQuote(this.Quote().VendorId, this.Quote().Id)
            .pipe(finalize(() => loadingRef.Stop()))
            .subscribe(() => {
              this.snackbar.open("Quote deleted successfully.", null, {
                duration: 5000,
              });
              this.dialogRef.close({
                QuoteId: null
              });
            });
        }
      });
    });
  }

  public AddInvoice() {
    const loadingRef = this.loadingService.StartWaiting("Creating Invoice");
    this.invoiceModalService.CreateInvoice(this.Quote().VendorId, <InvoiceCreate>{
      InvoiceStatus: InvoiceStatus.New,
      Year: this.Quote().Year,
      PurchaseOrderNumber: this.Quote().Status == QuoteStatus.PurchaseOrder ? this.Quote().PurchaseOrderNumber : null,
      PurchaseRequisitionNumber: this.Quote().PurchaseRequisitionNumber ?? ""
    })
      .pipe(finalize(() => loadingRef.Stop()))
      .subscribe({ 
        next: (result: Invoice) => {
          this.dialogRef.close({
            QuoteId: this.Quote().Id,
            InvoiceIdToDisplay: result.Id
          });
        }, 
        error: () => {
          this.snackbar.open("There was a problem creating this invoice.", null, {
            duration: 5000,
          });
        }
      });
  }

  protected isInvoiceButtonDisabled() {
    return this.Quote() == null
      || this.Quote().Status < QuoteStatus.PurchaseOrder
      || !this.Quote().VendorActive
      || this.changeDetectionService.HasChanges(this.GetChangeDetectionData())
      || !this.UserIsAdmin;
  }

  private CheckForChangesAndPrompt(): Observable<boolean> {
    if (this.changeDetectionService.HasChanges(this.GetChangeDetectionData())) {
      return this.changeDetectionService.HasChangeDialog();
    }
    return of(true);
  }

  protected editVendor(vendorId: number) {
    if (!this.isFromVendor) {
      this.vendorDetailModal.ShowModal({
        vendorId: vendorId,
        isFromQuote: true
      });
    }

  }

  protected ExportAriba() {
    const urlTree = this.router.createUrlTree([`api/v1/vendors/${this.Quote().VendorId}/quotes/${this.Quote().Id}/ariba-export`], {});
    const fileDownloadLink = window.location.origin + urlTree.toString();
    window.open(fileDownloadLink, "_blank"); // "_self"" was making the user refresh the page to see the modal again.
  }
}

@Injectable()
export class QuoteEditModal {
  private dialog = inject(MatDialog);

  public ShowStandaloneModal = (_data: Data, params: Params): Observable<any> => this.ShowModal({
    VendorId: Number(params.get('vendorId')),
    QuoteId: Number(params.get('quoteId'))
  });

  /** Displays the Quote modal. Returns the Quote id when the user closes the modal, or undefined if no Quote was created
   * @returns {Observable<number | undefined>} The Quote ID or undefined if no vendor was created.
   */
  public ShowModal = (data: ShowQuoteEditModalData): Observable<QuoteCloseModalResult | undefined> => {
    const ref = this.dialog.open(QuoteEditModalComponent, {
      data: data,
      width: "1500px",
      disableClose: true,
      autoFocus: false,
      panelClass: ["quote-edit-modal-panel", "standard-modal-panel"],
    });

    return ref.afterClosed();
  }
}
